var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { msg } from '@lit/localize';
import { map } from 'lit/directives/map.js';
import { html, render } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { v4 as uuidv4 } from 'uuid';
import { BaseShadowless } from '../indicator/BaseShadowless';
/**
 * Represents the menu to show in a layer item
 */
let DinapsisMapLayerToolbar = class DinapsisMapLayerToolbar extends BaseShadowless {
    constructor() {
        super(...arguments);
        /**
        * The id of the layer item.
        */
        this.layerId = '';
        /**
        * The id of the layer item.
        */
        this.isVisible = true;
        /**
         * The options of the menu that should be shown for each layer.
         */
        this.layerToolbarConfig = {
            showVisibility: true,
            showOpacity: true,
            showDelete: true,
        };
        /**
         * If we show item menu or not
         */
        this.showMenu = false;
        this.left = 0;
        this.top = 0;
        /**
         * Private property to generate a uniqueid for popup container
         */
        this.popupContainerId = `popup-container-${uuidv4()}`;
        /**
         * Private property to generate a uniqueid for toolbar container
         */
        this.toolbarId = `toolbar-container-${uuidv4()}`;
    }
    /**
     * Manage clicks outside element
     */
    connectedCallback() {
        super.connectedCallback();
        // Bind hideMenu function to this
        this.clickHideMenu = this.clickHideMenu.bind(this);
        // eslint-disable-next-line no-restricted-globals
        addEventListener('click', this.clickHideMenu);
    }
    /**
     * Remove click handler
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('click', this.clickHideMenu);
    }
    /**
     * Launch event to parent that visibility has to change.
     */
    changeVisibility() {
        this.dispatchEvent(new CustomEvent('change-visibility', { detail: { isVisible: !this.isVisible, layerId: this.layerId } }));
    }
    /**
     * Launch evnt when clicked on a menu option
     * @param id the name of the event/ id of the item clicked
     */
    itemClicked(id) {
        this.dispatchEvent(new CustomEvent('option-clicked', { detail: { optionId: id, layerId: this.layerId } }));
        this.showMenu = false;
        this.removePopupBody();
    }
    /**
     * Toggle menu options.
     */
    toggleMenu() {
        this.removePopupBody();
        this.showMenu = !this.showMenu;
        if (this.showMenu) {
            // If we are going to show menu, calculate its position
            this.calculateMenuPosition();
            // Create Popup Element
            this.createPopupBody();
        }
    }
    /**
     * This function creates a popup body element with a specified ID,
     * class, position, and content, and appends it to the document body.
     */
    createPopupBody() {
        var _a;
        // New element and set properties
        const element = document.createElement('div');
        element.id = this.popupContainerId;
        element.className = 'popup-container-map';
        element.style.top = `${this.top}px`;
        element.style.left = `${this.left}px`;
        // Set render
        render(this.renderMenuOption(), element);
        // Append to map
        (_a = this.closest('.dinapsis-map')) === null || _a === void 0 ? void 0 : _a.appendChild(element);
    }
    /**
     * This function removes if exists a popup body element from the DOM.
     */
    removePopupBody() {
        const element = document.getElementById(this.popupContainerId);
        if (!element)
            return;
        element.remove();
    }
    /**
     * Calculate the menu position
     */
    calculateMenuPosition() {
        var _a, _b;
        // Constants
        const menuWidth = 135;
        const menuHeight = 44;
        const dotsIconWidth = 24;
        const margin = 7;
        // Get dots position
        const dots = this.getElementsByClassName('menu-container')[0];
        // Get dinapsis map
        const dinapsisMap = this.closest('.dinapsis-map');
        const mapRect = (_a = dinapsisMap === null || dinapsisMap === void 0 ? void 0 : dinapsisMap.getBoundingClientRect()) !== null && _a !== void 0 ? _a : { top: 0, left: 0 };
        const elemRect = dots.getBoundingClientRect();
        const offsetY = elemRect.top - mapRect.top;
        const offsetX = elemRect.left - mapRect.left;
        let bottomMapOffset = Infinity;
        const mapTop = mapRect.top;
        const mapHeight = (_b = dinapsisMap === null || dinapsisMap === void 0 ? void 0 : dinapsisMap.offsetHeight) !== null && _b !== void 0 ? _b : 0;
        bottomMapOffset = mapTop + mapHeight;
        // Calculate position
        this.left = offsetX - menuWidth + dotsIconWidth;
        if (bottomMapOffset > (offsetY + menuHeight)) {
            // Menu below map
            this.top = offsetY + menuHeight / 2 + margin;
        }
        else {
            // Menu above map
            this.top = offsetY - menuHeight + margin;
        }
    }
    /**
     * Hide the menu
     */
    clickHideMenu(event) {
        // If user clicks inside the element, do nothing
        if (event.target.closest(`#${this.toolbarId}`))
            return;
        // If user clicks outside the element and it is being shown, hide it
        if (this.showMenu) {
            this.showMenu = false;
            this.removePopupBody();
        }
    }
    /**
     * Get the summary of options that will be shoen in the menu in order to know
     * its number and its info
     * @returns The total menu options number and their info
     */
    getMenuOptions() {
        const result = [];
        if (this.layerToolbarConfig.showOpacity) {
            result.push({ id: 'change-opacity', icon: 'opacity', name: msg('opacity') });
        }
        if (this.layerToolbarConfig.showDelete) {
            result.push({ id: 'delete-layer', icon: 'delete', name: msg('map_delete') });
        }
        if (this.layerToolbarConfig.customOptions) {
            this.layerToolbarConfig.customOptions.forEach((opt) => {
                result.push({ id: opt.id, icon: opt.icon, name: opt.name });
            });
        }
        return result;
    }
    /**
     * Returns the html of a menu item configuring icon and text
     * @param id The item and event name
     * @param icon The material icon name
     * @param text The text of the option menu
     * @returns The html of a menu item
     */
    getMenuItem(id, icon, text) {
        return html `
      <div class="layer-popup-menu-item ${id}"  @click=${() => this.itemClicked(id)}>
        <span class="material-icons-round dinapsis-map-icon icon ${icon}">
          ${icon}
        </span>
        <span class="text">
          ${text}
        </span>
      </div>
    `;
    }
    /**
     * Return the html of a menu options if is visible
     * @returns The html for the menu options
     */
    renderMenuOption() {
        return html `
      <div class="layer-popup-menu" style="left:${this.left}px;top:${this.top}px">
        ${this.layerToolbarConfig.showOpacity ? this.getMenuItem('change-opacity', 'opacity', msg('opacity')) : ''}
        ${this.layerToolbarConfig.showDelete ? this.getMenuItem('delete-layer', 'delete', msg('map_delete')) : ''}
        ${this.renderCustomOptions()}
      </div>`;
    }
    /**
     * Return the html of a custom options for the menu options
     * @returns The html for the custom options
     */
    renderCustomOptions() {
        return html `
    ${map(this.layerToolbarConfig.customOptions, (customOption) => html `
      ${this.getMenuItem(customOption.id, customOption.icon, customOption.name)}
      `)}
    `;
    }
    /**
     * If there are many menu options, paint the three dots icon and the popup menu.
     * If there is only one option, paint only the icon of the option.
     * @returns The template to paint next to visibility (three dots or another icon)
     */
    renderOptionOrDotsAndMenu() {
        const totalMenuOptions = this.getMenuOptions();
        if (totalMenuOptions.length > 1) {
            // Paint three dots and popup menu
            return html `
      <div class="menu-container">
        <span class="material-icons-round dinapsis-map-icon more-options" @click=${this.toggleMenu}>
          more_horiz
        </span>
      </div>`;
        }
        if (totalMenuOptions.length === 1) {
            // Paint only an icon
            const opt = totalMenuOptions[0];
            return html `
        <span class="material-icons-round dinapsis-map-icon" title=${opt.name} @click=${() => this.itemClicked(opt.id)}>
            ${opt.icon}
        </span>`;
        }
        return html ``;
    }
    /**
     * The function returns a string of HTML that contains a div that contains the component.
     * @returns The return statement is returning the html template literal
     */
    render() {
        return html `
    <span class="layer-item-menu" id="${this.toolbarId}">
        <div class="visibility-container">
         ${this.layerToolbarConfig.showVisibility ? html `<span class="material-icons-round dinapsis-map-icon visibility" @click=${this.changeVisibility}>
              ${this.isVisible ? 'visibility' : 'visibility_off'}
          </span>` : ''}
        </div>
        ${this.renderOptionOrDotsAndMenu()}
    </span>
          `;
    }
};
__decorate([
    property({ type: String })
], DinapsisMapLayerToolbar.prototype, "layerId", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisMapLayerToolbar.prototype, "isVisible", void 0);
__decorate([
    property({ type: Object })
], DinapsisMapLayerToolbar.prototype, "layerToolbarConfig", void 0);
__decorate([
    state()
], DinapsisMapLayerToolbar.prototype, "showMenu", void 0);
DinapsisMapLayerToolbar = __decorate([
    customElement('dinapsis-map-layer-toolbar')
], DinapsisMapLayerToolbar);
export { DinapsisMapLayerToolbar };
//# sourceMappingURL=dinapsis-map-layer-toolbar.js.map